import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DollarSign, Users, Package, ShoppingBag, Crown, Star, Truck, Receipt, ShieldAlert } from "lucide-react";

const TX_TYPE_LABEL = {
  cart_purchase: "Marketplace sale",
  listing_fee: "Listing fee",
  subscription: "Pro subscription",
  feature_listing: "Boost",
};
const TX_TYPE_COLOR = {
  cart_purchase: "#FF007F",
  listing_fee: "#FFB800",
  subscription: "#CCFF00",
  feature_listing: "#05D9E8",
};

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="card-surface p-6">
      <Icon className="w-4 h-4 mb-3" style={{ color }} />
      <p className="text-xs uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="font-display font-black text-3xl mt-1" style={{ color }}>{value}</p>
    </div>
  );
}

function Tab({ name, current, onClick, count }) {
  const active = name === current;
  return (
    <button
      data-testid={`admin-tab-${name}`}
      onClick={() => onClick(name)}
      className={`px-4 py-2 text-xs uppercase tracking-widest border ${active ? "border-[#FF007F] text-[#FF007F]" : "border-white/10 text-zinc-400 hover:text-white"}`}
    >
      {name}{typeof count === "number" ? ` (${count})` : ""}
    </button>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/stats")
      .then(r => setStats(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
    api.get("/admin/products").then(r => setProducts(r.data || [])).catch(() => {});
    api.get("/admin/transactions").then(r => setTransactions(r.data || [])).catch(() => {});
    api.get("/admin/users").then(r => setUsers(r.data || [])).catch(() => {});
  }, [user]);

  if (loading) return <div className="pt-32 text-center text-zinc-500" data-testid="admin-loading">Loading…</div>;
  if (denied || (user && user.role !== "admin")) {
    return (
      <div className="pt-32 text-center" data-testid="admin-denied">
        <ShieldAlert className="w-10 h-10 text-red-400 mx-auto mb-4" />
        <p className="text-zinc-300">Admin access only.</p>
        <Link to="/" className="btn-secondary inline-block mt-6">Back home</Link>
      </div>
    );
  }
  if (!stats) return <div className="pt-32 text-center text-zinc-500">Loading admin…</div>;

  const rev = stats.revenue_by_type || {};
  const get = (k) => rev[k]?.total || 0;
  const getN = (k) => rev[k]?.count || 0;

  return (
    <div data-testid="admin-page" className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Host console</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 mb-10">Admin</h1>

      {/* High-level stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={DollarSign} label="Grand total" value={`$${stats.grand_total_usd.toFixed(2)}`} color="#CCFF00" />
        <Stat icon={Users} label="Users" value={stats.users_count} color="#05D9E8" />
        <Stat icon={Crown} label="Sellers" value={stats.sellers_count} color="#FF007F" />
        <Stat icon={Package} label="Listings" value={`${stats.published_count}/${stats.products_count}`} color="#FFB800" />
      </div>

      {/* Revenue breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Stat icon={ShoppingBag} label={`${TX_TYPE_LABEL.cart_purchase}s · ${getN("cart_purchase")}`} value={`$${get("cart_purchase").toFixed(2)}`} color={TX_TYPE_COLOR.cart_purchase} />
        <Stat icon={Receipt} label={`${TX_TYPE_LABEL.listing_fee}s · ${getN("listing_fee")}`} value={`$${get("listing_fee").toFixed(2)}`} color={TX_TYPE_COLOR.listing_fee} />
        <Stat icon={Crown} label={`${TX_TYPE_LABEL.subscription}s · ${getN("subscription")}`} value={`$${get("subscription").toFixed(2)}`} color={TX_TYPE_COLOR.subscription} />
        <Stat icon={Star} label={`${TX_TYPE_LABEL.feature_listing}s · ${getN("feature_listing")}`} value={`$${get("feature_listing").toFixed(2)}`} color={TX_TYPE_COLOR.feature_listing} />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Tab name="overview" current={tab} onClick={setTab} />
        <Tab name="listings" current={tab} onClick={setTab} count={products.length} />
        <Tab name="transactions" current={tab} onClick={setTab} count={transactions.length} />
        <Tab name="users" current={tab} onClick={setTab} count={users.length} />
      </div>

      {tab === "overview" && (
        <div className="card-surface p-8">
          <p className="text-zinc-400 leading-relaxed">
            Welcome, host. Use the tabs above to inspect everything that's happening on GlowCart:
          </p>
          <ul className="mt-4 text-sm text-zinc-400 space-y-2 list-disc list-inside">
            <li><span className="text-white">Listings</span> — every product on the site with the seller's name + email.</li>
            <li><span className="text-white">Transactions</span> — every Stripe payment (purchases, listing fees, subscriptions, boosts) with status.</li>
            <li><span className="text-white">Users</span> — everyone who's signed up + how many listings/orders they have.</li>
          </ul>
          <p className="text-xs text-zinc-500 mt-6">Stripe payouts land in the bank account connected to <span className="text-white">your</span> Stripe dashboard. View them at <a className="text-[#05D9E8] hover:underline" href="https://dashboard.stripe.com/payments" target="_blank" rel="noreferrer">dashboard.stripe.com/payments</a>.</p>
        </div>
      )}

      {tab === "listings" && (
        <div className="overflow-x-auto border border-white/5" data-testid="admin-listings-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/5">
                <th className="p-3">Product</th>
                <th className="p-3">Seller</th>
                <th className="p-3">Email</th>
                <th className="p-3">Cat</th>
                <th className="p-3">Price</th>
                <th className="p-3">Status</th>
                <th className="p-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-product-${p.id}`}>
                  <td className="p-3"><Link to={`/product/${p.id}`} className="hover:text-[#FF007F]">{p.title}</Link></td>
                  <td className="p-3">{p.seller_name}{p.seller_is_pro && <span className="ml-1 text-[#CCFF00]" title="Pro seller">★</span>}</td>
                  <td className="p-3 text-zinc-400 font-mono text-xs">{p.seller_email || "—"}</td>
                  <td className="p-3 text-zinc-400">{p.category}</td>
                  <td className="p-3 text-[#FF007F]">${Number(p.price).toFixed(2)}</td>
                  <td className="p-3">{p.is_published
                    ? <span className="text-[10px] uppercase tracking-widest text-[#CCFF00] border border-[#CCFF00]/30 px-2 py-0.5">Live</span>
                    : <span className="text-[10px] uppercase tracking-widest text-[#FFB800] border border-[#FFB800]/30 px-2 py-0.5">Unpaid</span>}
                  </td>
                  <td className="p-3 text-zinc-500 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {products.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-zinc-500">No listings yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "transactions" && (
        <div className="overflow-x-auto border border-white/5" data-testid="admin-transactions-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/5">
                <th className="p-3">When</th>
                <th className="p-3">Type</th>
                <th className="p-3">User</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Status</th>
                <th className="p-3">Session</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(t => (
                <tr key={t.session_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-tx-${t.session_id}`}>
                  <td className="p-3 text-zinc-400 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <span className="text-[10px] uppercase tracking-widest border px-2 py-0.5" style={{ borderColor: (TX_TYPE_COLOR[t.type] || "#fff")+"66", color: TX_TYPE_COLOR[t.type] || "#fff" }}>
                      {TX_TYPE_LABEL[t.type] || t.type}
                    </span>
                  </td>
                  <td className="p-3 text-zinc-400 font-mono text-xs">{t.user_email}</td>
                  <td className="p-3 text-[#FF007F] font-bold">${Number(t.amount).toFixed(2)}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 ${
                      t.payment_status === "paid" ? "text-[#CCFF00] border-[#CCFF00]/30"
                      : t.payment_status === "pending" ? "text-[#FFB800] border-[#FFB800]/30"
                      : "text-zinc-500 border-white/10"
                    }`}>{t.payment_status}</span>
                  </td>
                  <td className="p-3 text-zinc-500 font-mono text-[10px]">{t.session_id.slice(0, 20)}…</td>
                </tr>
              ))}
              {transactions.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">No transactions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "users" && (
        <div className="overflow-x-auto border border-white/5" data-testid="admin-users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/5">
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Role</th>
                <th className="p-3">Listings</th>
                <th className="p-3">Orders</th>
                <th className="p-3">Spent</th>
                <th className="p-3">Pro</th>
                <th className="p-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-user-${u.id}`}>
                  <td className="p-3">{u.name}</td>
                  <td className="p-3 text-zinc-400 font-mono text-xs">{u.email}</td>
                  <td className="p-3"><span className="text-[10px] uppercase tracking-widest text-zinc-400">{u.role}</span></td>
                  <td className="p-3">{u.listings_count}</td>
                  <td className="p-3">{u.orders_count}</td>
                  <td className="p-3 text-[#FF007F]">${Number(u.total_spent || 0).toFixed(2)}</td>
                  <td className="p-3">{u.seller_subscription_active ? <span className="text-[#CCFF00]">★</span> : "—"}</td>
                  <td className="p-3 text-zinc-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-zinc-500">No users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
